import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { AppService } from '../../services/app.service';
import { HttpService } from '../../services/http.service';
import { PermissionService } from '../../services/permission.service';
import { SnackBarService } from '../../services/snack-bar.service';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AdditionalAttachment } from '../../../next-steps/next-steps.data.service';
import { UploadAttachmentControl } from '../documents-selection/documents-selection.component';
import { environment } from '../../../../environments/environment';
import { PrintTypeLong } from '../../models/print-type';

const EMAIL_SEPERATOR = /[,;]/; // separate by "," or ";"

function commaSeparatedEmailsValidator(control: AbstractControl): ValidationErrors {
    const errors = (<string>control.value)
        .split(EMAIL_SEPERATOR)
        .map((email) => Validators.email(<AbstractControl>{ value: email.trim() }))
        .filter((v) => v !== null);
    if (errors.length) {
        return { email: true };
    }
    return null;
}

export const atLeastOneValidator = (keys: string[]) => {
    return (group: FormGroup) => {
        const { controls } = group;
        return keys.some((key) => {
            const value = controls[key] && controls[key].value;
            return value && (!Array.isArray(value) || value.length > 0);
        })
            ? null
            : { atLeastOne: 'error' };
    };
};

@Component({
    selector: 'app-send-documents-via-email-dialog',
    templateUrl: './send-documents-via-email-dialog.component.html',
    styleUrls: ['./send-documents-via-email-dialog.component.scss'],
})
export class SendDocumentsViaEmailDialogComponent implements OnInit {
    public sendEmailForm = new FormGroup<{
        emailSelect: FormControl<string>;
        email: FormControl<string>;
        emailCC: FormControl<string>;
        emailBCC: FormControl<string>;
        emailBody: FormControl<string>;
    }>(
        {
            emailSelect: new FormControl<string>('', []),
            email: new FormControl<string>('', [commaSeparatedEmailsValidator]),
            emailCC: new FormControl<string>('', [commaSeparatedEmailsValidator]),
            emailBCC: new FormControl<string>('', [commaSeparatedEmailsValidator]),
            emailBody: new FormControl<string>(''),
        },
        { validators: atLeastOneValidator(['emailSelect', 'email']) }
    );
    public isLoading = false;
    public sanitizedHtml: SafeHtml;
    public showMailCopyOptions = false;
    constructor(
        @Inject(MAT_DIALOG_DATA) public data: SendDocumentSettings,
        public dialogRef: MatDialogRef<SendDocumentsViaEmailDialogComponent>,
        private snackBarService: SnackBarService,
        private translateService: TranslateService,
        private permissionService: PermissionService,
        private appService: AppService,
        private httpService: HttpService,
        private sanitizer: DomSanitizer
    ) {}

    public ngOnInit() {
        if (this.permissionService.userInfo$.value && this.permissionService.userInfo$.value.mail) {
            const prefillControl = this.permissionService.isUserAnEmployee
                ? this.sendEmailForm.controls.emailCC
                : this.sendEmailForm.controls.email;
            prefillControl.setValue(this.permissionService.userInfo$.value.mail);
        }
        this.sanitizedHtml = this.sanitizer.bypassSecurityTrustHtml(this.data.mailText || '');
    }

    public onYesClick() {
        this.isLoading = true;
        this.sendMail();
    }

    private setControlsEnabled(enabled: boolean) {
        Object.values(this.sendEmailForm.controls).forEach((control) => {
            if (enabled) {
                control.enable();
            } else {
                control.disable();
            }
        });
    }

    public isEmployee() {
        return this.permissionService.isUserAnEmployee;
    }

    private sendMail() {
        this.setControlsEnabled(false);
        const { emailSelect, email, emailCC, emailBCC, emailBody } = this.sendEmailForm.value as {
            emailSelect: string;
            email: string;
            emailCC: string;
            emailBCC: string;
            emailBody: string;
        };
        const toEmailList = (mail: string) =>
            mail
                .split(EMAIL_SEPERATOR)
                .map((v) => v.trim())
                .filter((v) => !!v);

        const mails = toEmailList(email);

        const mergeEmailLists = (originalList, additionalList) =>
            [...new Set([...originalList, ...additionalList])].filter((v) => !!v);

        this.sendMailWithDocuments({
            emails: mergeEmailLists(mails, emailSelect),
            emailsCC: toEmailList(emailCC),
            emailsBCC: toEmailList(emailBCC),
            emailBody,
        }).subscribe(
            () => {
                this.snackBarService.openSnackBar({
                    message: this.translateService.instant('NEXT_STEPS.EMAIL_DIALOG.SENDING_DOCUMENTS.SUCCESS_MSG'),
                    isFailure: false,
                });
                this.dialogRef.close();
                this.isLoading = false;
                this.setControlsEnabled(true);
            },
            () => {
                this.snackBarService.openSnackBar({
                    message: this.translateService.instant('NEXT_STEPS.EMAIL_DIALOG.SENDING_DOCUMENTS.FAILURE_MSG'),
                    isFailure: true,
                });
                this.isLoading = false;
                this.setControlsEnabled(true);
            }
        );
    }

    public sendMailWithDocuments({
        emails,
        emailsCC,
        emailsBCC,
        emailBody,
    }: {
        emails: Array<string>;
        emailsCC: Array<string>;
        emailsBCC: string[];
        emailBody: string;
    }) {
        const id = this.data.quoteId;
        const url = this.data.isLegacy
            ? `${environment.http.baseUrl}quote/${id}/revision/0/mail`
            : `${environment.http.quotation}quotations/quote-assist/quotes/${id}/mail`;

        const additionalOfferAttachments = this.data.additionalOfferAttachments.map((a) => a.id);
        const payload = {
            emailReceivers: emails,
            emailCCReceivers: emailsCC,
            emailBCCReceivers: emailsBCC,
            emailBody,
            exportFormats: this.data.selectedExportFormats,
            language: this.appService.getLanguageOnly(),
            country: this.appService.getLocation(),
            ...(this.data.isLegacy && {
                sendEmailToSalesRep: false,
                salesOrganization: this.appService.getSalesOrg(),
                offerAttachments: this.data.offerAttachments,
                additionalOfferAttachments,
            }),
            ...(!this.data.isLegacy && {
                salesOrg: this.appService.getSalesOrg(),
                offerAttachments: additionalOfferAttachments,
                archiveDocumentUris: this.data.offerAttachments.map((oa) => oa.documentUri),
            }),
            schemes: this.data.schemes,
            schemesDwt: this.data.schemesDwt,
            ...(this.data.energyLabelSelected && { energyLabel: this.data.energyLabel }),
            printType: this.data.printType,
        };

        return this.httpService.post(url, payload);
    }

    public toggleMailCopyOptions() {
        this.showMailCopyOptions = !this.showMailCopyOptions;
    }
}

export interface SendDocumentSettings {
    selectedExportFormats: string[];
    energyLabelSelected: boolean;
    energyLabel: string;
    schemes: string[];
    schemesDwt: string[];
    quoteId: string;
    offerAttachments: UploadAttachmentControl[];
    additionalOfferAttachments: AdditionalAttachment[];
    mailText: string;
    mailRecipients: string[];
    printType: PrintTypeLong;
    isLegacy: boolean;
}
